/*
  <lista-carte> — tendina, ricerca, filtro rarita' e ingrandimento della sezione
  sections/lista-carte.liquid.

  Stati:
  - CHIUSA: si vede solo la striscia delle prime carte (stampata da Liquid). Nessuna carta
    della lista esiste ancora nel DOM.
  - APERTA: ricerca, rarita' e le prime N carte (data-iniziali), con «Mostra tutte».
  - TUTTE: dopo «Mostra tutte», o con una ricerca/rarita' attiva (che mostra sempre TUTTE le
    corrispondenze, anche oltre le prime N).
  «Nascondi» (barra in fondo, sempre a portata: e' sticky) torna a CHIUSA, azzera ricerca e
  rarita' e riporta la pagina all'inizio della sezione — dopo aver chiuso 300 carte la
  pagina si accorcia di colpo e senza questo ci si ritroverebbe in mezzo al footer.

  Il set arriva come JSON in <script type="application/json" data-carte>. Le carte si creano
  quando servono: le prime N all'apertura, le altre alla prima ricerca o a «Mostra tutte»;
  da li' in poi filtrare e' solo accendere/spegnere `hidden`. Immagini loading="lazy".
*/
(() => {
  if (customElements.get('lista-carte')) return;

  // minuscole e senza accenti: "pokemon" trova "Pokémon"
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

  class ListaCarte extends HTMLElement {
    connectedCallback() {
      let dati;
      try {
        dati = JSON.parse(this.querySelector('[data-carte]').textContent);
      } catch (e) {
        return;
      }
      /*
        Formato compatto (set con centinaia di carte, es. Start Deck 100: 782): il prefisso comune
        degli URL sta una volta sola in «base» e ogni carta ha solo «f» (file + ?v=). Qui si
        ricompongono i due URL, cosi' il resto del codice vede sempre c.i / c.h.
      */
      if (dati.base) {
        for (const c of dati.carte || []) {
          if (c.i || !c.f) continue;
          const u = dati.base + c.f + (c.f.includes('?') ? '&' : '?');
          c.i = u + 'width=245';
          c.h = u + 'width=700';
        }
      }

      this.set = dati.set || [];
      this.doppia = this.set.length > 1;
      /*
        Si cerca nel nome E nel codice: One Piece e Dragon Ball non hanno «001/142» ma un
        codice («OP17-001», «EB04-061»), quindi «eb04» deve trovare la ristampa. Il numero e'
        l'ultimo gruppo di cifre: «61» trova OP17-061 come trova la 061/142 dei Pokemon.
      */
      this.items = (dati.carte || []).map((c) => ({
        c,
        nome: norm(c.t + ' ' + c.n),
        num: parseInt((String(c.n).match(/(\d+)\D*$/) || [])[1], 10),
        el: null
      }));
      // il JSON e' in ordine di numero; «dalla piu' rara» = al contrario (le rare hanno i numeri alti)
      if (this.dataset.ordine !== 'asc') this.items.reverse();
      this.creati = 0;

      this.apriBtn = this.querySelector('[data-apri]');
      this.corpo = this.querySelector('[data-corpo]');
      this.griglia = this.querySelector('[data-griglia]');
      this.input = this.querySelector('[data-cerca]');
      this.select = this.querySelector('[data-rarita]');
      this.clearBtn = this.querySelector('[data-svuota]');
      this.mostraBtn = this.querySelector('[data-mostra-tutte]');
      this.nascondiBtn = this.querySelector('[data-nascondi]');
      this.status = this.querySelector('[data-stato]');
      this.empty = this.querySelector('[data-vuoto]');
      this.dialog = this.querySelector('[data-zoom-dialog]');

      /*
        Immagine che non si carica → il nome della carta al suo posto, e niente zoom. Succede
        quando TCGdex elenca una carta ma non ha ancora la scansione (i tre Mew «RGB Rare» del
        30esimo, ottobre 2026): il link resta quello, quando TCGdex la carica torna da sola.
        L'evento 'error' non risale: lo si prende in cattura. Le immagini della striscia arrivano
        col HTML, prima di questo script: quelle gia' fallite si riconoscono da complete + 0 px.
      */
      this.addEventListener('error', (e) => {
        if (e.target && e.target.tagName === 'IMG') this.immagineMancante(e.target);
      }, true);
      this.querySelectorAll('.lista-carte__peek-card img').forEach((img) => {
        if (img.complete && !img.naturalWidth) this.immagineMancante(img);
      });

      this.iniziali = parseInt(this.dataset.iniziali, 10) || 0;
      this.sizes = this.dataset.sizes || '';
      this.mostraNumero = this.dataset.numero === 'true';
      this.mostraRarita = this.dataset.raritaVisibile === 'true';
      this.zoom = this.dataset.zoom === 'true' && !!this.dialog;
      this.aperta = false;
      this.espansa = false;

      this.apriBtn.addEventListener('click', () => this.apri());
      this.nascondiBtn.addEventListener('click', () => this.chiudi());

      if (this.mostraBtn) {
        this.mostraBtn.addEventListener('click', () => {
          this.espansa = true;
          this.applica();
        });
      }

      if (this.input) this.input.addEventListener('input', () => this.applica());
      if (this.select) this.select.addEventListener('change', () => this.applica());

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.applica();
        });
      }

      if (this.zoom) {
        this.griglia.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-zoom]');
          if (btn) this.ingrandisci(this.items[parseInt(btn.dataset.indice, 10)].c, btn);
        });

        this.zoomImg = this.dialog.querySelector('[data-zoom-img]');

        /*
          Toccando la carta, il fondo scuro o la X la carta torna al suo posto nella lista con
          lo stesso volo del trascinamento (richiesta del proprietario 01/10) — ma non alla
          fine di un trascinamento, che ha gia' deciso da se'.
        */
        this.dialog.addEventListener('click', (e) => {
          if (this.trascinato) { this.trascinato = false; return; }
          if (this.chiudendo) return;
          if (e.target === this.dialog || e.target === this.zoomImg || e.target.closest('[data-chiudi]')) this.chiudiZoom();
        });

        // Esc: stesso volo invece della chiusura secca del browser
        this.dialog.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.chiudiZoom();
        });

        // trascinamento della carta (dito o mouse): vedi inizioTrascina / fineTrascina
        this.dialog.addEventListener('pointerdown', (e) => this.inizioTrascina(e));
        this.dialog.addEventListener('pointermove', (e) => this.trascina(e));
        this.dialog.addEventListener('pointerup', () => this.fineTrascina());
        this.dialog.addEventListener('pointercancel', () => this.annullaTrascina());

        // iOS vecchi ignorano touch-action: senza questo la pagina sotto scorrerebbe col dito
        this.dialog.addEventListener('touchmove', (e) => { if (e.cancelable) e.preventDefault(); }, { passive: false });

        this.dialog.addEventListener('wheel', () => this.chiudiZoom(), { passive: true });
        this.dialog.addEventListener('close', () => this.ripulisciZoom());

        // se la pagina si sposta per altri motivi (tastiera, ecc.) la carta aperta non ha piu' senso
        this.onScrollZoom = () => {
          if (this.dialog.open && !this.chiudendo && !this.gesto && Math.abs(window.scrollY - this.zoomY) > 10) this.dialog.close();
        };
        window.addEventListener('scroll', this.onScrollZoom, { passive: true });
      }

      // il menu rarita' si riadatta quando cambia larghezza (rotazione, finestra ridimensionata)
      if (this.select && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.adattaRarita());
        });
        this.ro.observe(this.select);
      }

      // nell'editor del tema: selezionando la sezione si apre, cosi' si vede cosa si modifica
      this.onEditor = (e) => { if (e.detail && e.detail.sectionId === this.dataset.sezione) this.apri(); };
      document.addEventListener('shopify:section:select', this.onEditor);

      this.ripristina();
    }

    /*
      🚩 Ricarica e «Indietro». Il browser rimette la pagina allo stesso punto in cui era. Ma
      la pagina ricaricata parte con la tendina CHIUSA: se prima era aperta su 300 carte
      (18.000px), il punto salvato non esiste piu' e si finiva nel footer.
      Quindi lo stato (aperta, «Tutte», ricerca, rarita') sta in sessionStorage e, SOLO su
      ricarica o avanti/indietro, si riapre la tendina com'era PRIMA che il browser rimetta
      lo scroll: la pagina torna alta uguale e si riatterra sulla stessa carta.
      Arrivando da un link la tendina parte chiusa come sempre. sessionStorage muore con la
      scheda; se non e' disponibile (navigazione privata, cookie bloccati) si ignora.
    */
    chiave() {
      return 'lista-carte:' + location.pathname + ':' + this.dataset.sezione;
    }

    ripristina() {
      let tipo = '';
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        tipo = nav ? nav.type : '';
      } catch (e) { /* browser vecchio: niente ripristino */ }
      if (tipo !== 'reload' && tipo !== 'back_forward') {
        // arrivo da un link: la tendina parte chiusa, e lo stato di una visita precedente va
        // buttato — se no una ricarica di QUESTA pagina (rimasta chiusa) la riaprirebbe
        try { sessionStorage.removeItem(this.chiave()); } catch (e) { /* niente */ }
        return;
      }

      let stato = null;
      try { stato = JSON.parse(sessionStorage.getItem(this.chiave()) || 'null'); } catch (e) { return; }
      if (!stato || !stato.aperta) return;

      if (this.input && stato.q) this.input.value = stato.q;
      if (this.select && stato.r) this.select.value = stato.r; // rarita' sparita dal set: torna a "Tutte"
      this.espansa = !!stato.espansa;
      this.apri();
    }

    salva() {
      try {
        if (this.aperta) {
          sessionStorage.setItem(this.chiave(), JSON.stringify({
            aperta: true,
            espansa: this.espansa,
            q: this.input ? this.input.value : '',
            r: this.select ? this.select.value : ''
          }));
        } else {
          sessionStorage.removeItem(this.chiave());
        }
      } catch (e) { /* storage pieno o bloccato: pazienza, si perde solo il ripristino */ }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.onScrollZoom) window.removeEventListener('scroll', this.onScrollZoom);
      document.removeEventListener('shopify:section:select', this.onEditor);
    }

    apri() {
      if (this.aperta) return;
      this.aperta = true;
      this.apriBtn.hidden = true;
      this.apriBtn.setAttribute('aria-expanded', 'true');
      this.corpo.hidden = false;
      this.applica();
      this.adattaRarita();
    }

    chiudi() {
      this.aperta = false;
      this.espansa = false;
      if (this.input) this.input.value = '';
      if (this.select) this.select.value = '';
      if (this.dialog && this.dialog.open) this.dialog.close();

      this.corpo.hidden = true;
      this.apriBtn.hidden = false;
      this.apriBtn.setAttribute('aria-expanded', 'false');
      this.applica();

      /*
        Se l'inizio della sezione e' sopra lo schermo, ci si torna (sotto l'header fisso).
        'instant' e non 'auto': il tema mette scroll-behavior: smooth su <html>, e con 'auto'
        la pagina — appena accorciata di migliaia di pixel — scorreva per un secondo
        passando dal footer prima di arrivare alla sezione.
      */
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const top = this.closest('.shopify-section') ? this.closest('.shopify-section').getBoundingClientRect().top : this.getBoundingClientRect().top;
      if (top < header) window.scrollTo({ top: window.scrollY + top - header - 12, behavior: 'instant' });

      this.apriBtn.focus({ preventScroll: true });
    }

    // crea gli elementi delle carte fino all'indice `fino` (escluso), in ordine
    crea(fino) {
      if (fino <= this.creati) return;
      const frag = document.createDocumentFragment();
      for (let i = this.creati; i < fino; i++) {
        const it = this.items[i];
        it.el = this.carta(it.c, i);
        frag.appendChild(it.el);
      }
      this.griglia.appendChild(frag);
      this.creati = fino;
    }

    carta(c, i) {
      const li = document.createElement('li');
      li.className = 'lista-carte__item';

      const nome = esc(c.t);
      const set = this.set[c.s || 0] || {};
      // un <span> per pezzo: il CSS li mette uno per riga su mobile, in fila col punto su desktop
      const parti = [];
      if (this.mostraNumero) {
        if (this.doppia && set.nome) parti.push(set.nome);
        // "092/063", "001/086": il totale ha le stesse cifre del numero, come stampato sulla carta
        // carte senza numero (Yu-Gi-Oh Manga-Style, Energie base): niente pezzo vuoto, se no resta un « · » orfano
        // il totale solo sui numeri veri: «R/128» per il Mew «R» del 30esimo non e' stampato da nessuna parte
        if (c.n) parti.push(c.n + (set.ufficiali && /^\d+$/.test(c.n) ? '/' + String(set.ufficiali).padStart(String(c.n).length, '0') : ''));
      }
      if (this.mostraRarita && c.r) parti.push(c.r);

      const img = c.i
        ? `<img src="${esc(c.i)}"${c.h ? ` srcset="${esc(c.i)} 245w, ${esc(c.h)} 600w" sizes="${esc(this.sizes)}"` : ''} width="245" height="337" alt="${nome}" loading="lazy">`
        : `<span class="lista-carte__placeholder">${nome}</span>`;

      const card = this.zoom && c.h
        ? `<button type="button" class="lista-carte__card" data-zoom data-indice="${i}">${img}</button>`
        : `<div class="lista-carte__card">${img}</div>`;

      li.innerHTML = card
        + `<p class="lista-carte__name" title="${nome}">${nome}</p>`
        + (parti.length ? `<p class="lista-carte__info">${parti.map((p) => `<span>${esc(p)}</span>`).join('')}</p>` : '');
      return li;
    }

    applica() {
      const q = norm(this.input ? this.input.value : '');
      const r = this.select ? this.select.value : '';
      const filtra = q !== '' || r !== '';
      // "25" cerca la carta numero 25 (025/142) oltre ai nomi che contengono "25"
      const numero = /^\d+$/.test(q) ? parseInt(q, 10) : null;

      // da chiusa non si crea niente; aperta: tutte con un filtro o dopo «Mostra tutte», se no le prime N
      if (this.aperta) this.crea(filtra || this.espansa ? this.items.length : Math.min(this.iniziali, this.items.length));

      let trovate = 0;
      for (const it of this.items) {
        const ok = (!r || it.c.r === r) && (!q || it.nome.includes(q) || (numero !== null && it.num === numero));
        if (ok) trovate++;
        if (it.el) it.el.hidden = !(ok && (filtra || this.espansa || trovate <= this.iniziali));
      }

      if (this.clearBtn) this.clearBtn.hidden = q === '';
      if (this.mostraBtn) this.mostraBtn.hidden = filtra || this.espansa || this.items.length <= this.iniziali;
      if (this.empty) this.empty.hidden = trovate > 0;

      if (this.status) {
        this.status.textContent = !filtra || trovate === 0 ? ''
          : trovate === 1 ? this.dataset.testoUna
          : this.dataset.testoPiu.replace('{n}', trovate);
      }

      // ogni cambio di stato passa da qui: aperta/chiusa, «Tutte», ricerca, rarita'
      this.salva();
    }

    /*
      Voci del menu rarita' larghe quanto il menu chiuso, come la scelta variante nelle card
      (assets/variant-card-picker.js, fitOptions): se una voce non ci sta si accorcia il NOME
      e il numero di carte "(15)" resta sempre intero. Cosi' anche la lista aperta non
      sborda oltre il campo.
    */
    adattaRarita() {
      const sel = this.select;
      if (!sel || !sel.offsetParent) return; // nascosto: si misura alla prossima apertura

      const cs = getComputedStyle(sel);
      const avail = sel.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0) - 2;
      if (!(avail > 0)) return;

      const ctx = ListaCarte.ctx || (ListaCarte.ctx = document.createElement('canvas').getContext('2d'));
      ctx.font = [cs.fontStyle, cs.fontWeight, cs.fontSize, cs.fontFamily].join(' ');

      for (const option of sel.options) {
        const name = option.dataset.name || option.textContent;
        const tail = option.dataset.tail || '';
        const full = name + tail;

        if (ctx.measureText(full).width <= avail) {
          if (option.textContent !== full) option.textContent = full;
          continue;
        }

        // ricerca binaria sul NOME: la coda (il numero di carte) non si tocca mai
        let lo = 0;
        let hi = name.length;
        while (lo < hi) {
          const mid = Math.ceil((lo + hi) / 2);
          if (ctx.measureText(name.slice(0, mid).trimEnd() + '…' + tail).width <= avail) lo = mid;
          else hi = mid - 1;
        }

        // sotto 4 lettere il nome non dice piu' niente: meglio una voce un filo piu' larga
        const keep = Math.max(lo, Math.min(4, name.length));
        const next = name.slice(0, keep).trimEnd() + (keep < name.length ? '…' : '') + tail;
        if (option.textContent !== next) option.textContent = next;
      }
    }

    // vedi connectedCallback: solo le miniature della griglia e della striscia, non l'immagine dello zoom
    immagineMancante(img) {
      const box = img.closest('.lista-carte__card, .lista-carte__peek-card');
      if (!box) return;
      const segnaposto = document.createElement('span');
      segnaposto.className = 'lista-carte__placeholder';
      segnaposto.textContent = img.dataset.nome || img.alt || '';
      img.replaceWith(segnaposto);
      // il bottone dello zoom diventa un riquadro normale: non c'e' niente da ingrandire
      if (box.matches('button.lista-carte__card')) {
        const div = document.createElement('div');
        div.className = 'lista-carte__card';
        div.append(segnaposto);
        box.replaceWith(div);
      }
    }

    ingrandisci(c, btn) {
      const img = this.dialog.querySelector('[data-zoom-img]');
      const caption = this.dialog.querySelector('[data-didascalia]');

      // prima la miniatura (gia' in cache: si vede subito), poi l'immagine grande quando arriva
      const mini = btn.querySelector('img');
      img.src = (mini && mini.currentSrc) || c.i || c.h;
      img.alt = c.t || '';
      if (caption) caption.textContent = c.t || '';

      if (c.h && c.h !== img.src) {
        const pre = new Image();
        pre.onload = () => { if (img.alt === (c.t || '')) img.src = c.h; };
        pre.src = c.h;
      }

      this.zoomY = window.scrollY; // riferimento per chiudere se la pagina si sposta
      this.zoomBtn = btn;          // la carta della lista: e' li' che l'immagine torna
      this.alpha = 0.8;
      this.dialog.showModal();
    }

    /*
      ---- Trascinare la carta ingrandita ----
      Come la galleria foto del telefono:
      - la carta segue il dito in qualunque direzione e si rimpicciolisce man mano, il fondo
        si schiarisce;
      - riportandola al punto di partenza torna grande;
      - lasciandola oltre la soglia (o con un colpo secco) vola al suo posto nella lista; la
        lista resta ferma dov'era;
      - lasciandola prima della soglia torna al centro.
      Soglia in proporzione allo schermo e alla direzione: in verticale il 18% dell'altezza,
      in orizzontale il 25% della larghezza (lo schermo e' stretto, il dito fa meno strada).
    */
    soglia(dx, dy) {
      return Math.abs(dx) > Math.abs(dy) ? window.innerWidth * 0.25 : window.innerHeight * 0.18;
    }

    scalaPer(dx, dy) {
      const d = Math.hypot(dx, dy);
      return Math.max(0.5, 1 - d / (Math.max(window.innerWidth, window.innerHeight) * 1.1));
    }

    fondoPer(dx, dy) {
      const p = Math.min(1, Math.hypot(dx, dy) / this.soglia(dx, dy));
      return 0.8 * (1 - 0.7 * p);
    }

    posa(x, y, s, alpha) {
      this.zoomImg.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
      this.dialog.style.backgroundColor = `rgba(0, 0, 0, ${alpha})`;
      this.alpha = alpha;
    }

    inizioTrascina(e) {
      if (this.chiudendo || e.button > 0 || e.target.closest('[data-chiudi]')) return;
      this.gesto = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, attivo: false, campioni: [{ t: e.timeStamp, x: e.clientX, y: e.clientY }] };
    }

    trascina(e) {
      const g = this.gesto;
      if (!g || e.pointerId !== g.id) return;
      g.dx = e.clientX - g.x0;
      g.dy = e.clientY - g.y0;

      if (!g.attivo) {
        if (Math.hypot(g.dx, g.dy) < 8) return; // ancora un tocco, non un trascinamento
        g.attivo = true;
        try { this.dialog.setPointerCapture(e.pointerId); } catch (err) { /* evento sintetico */ }
        this.zoomImg.style.transition = 'none';
        this.dialog.style.transition = 'none';
        this.zoomRiposo = this.zoomImg.getBoundingClientRect(); // posizione da ferma, per il volo
        this.dialog.classList.add('is-trascinando');
      }

      g.campioni.push({ t: e.timeStamp, x: e.clientX, y: e.clientY });
      if (g.campioni.length > 6) g.campioni.shift();
      this.posa(g.dx, g.dy, this.scalaPer(g.dx, g.dy), this.fondoPer(g.dx, g.dy));
    }

    fineTrascina() {
      const g = this.gesto;
      this.gesto = null;
      if (!g || !g.attivo) return; // era un tocco: ci pensa il click

      // il click che arriva subito dopo il rilascio non deve chiudere; se non arriva, si azzera
      this.trascinato = true;
      setTimeout(() => { this.trascinato = false; }, 400);

      // velocita' sugli ultimi movimenti, in px/ms
      const a = g.campioni[0], b = g.campioni[g.campioni.length - 1];
      const dt = Math.max(1, b.t - a.t);
      const vx = (b.x - a.x) / dt, vy = (b.y - a.y) / dt;

      const orizzontale = Math.abs(g.dx) > Math.abs(g.dy);
      const dist = orizzontale ? Math.abs(g.dx) : Math.abs(g.dy);
      const v = orizzontale ? vx : vy;
      const spostamento = orizzontale ? g.dx : g.dy;
      // un colpo secco nella stessa direzione chiude anche prima della soglia
      const lancio = Math.abs(v) > 0.6 && dist > 30 && Math.sign(v) === Math.sign(spostamento);

      if (dist > this.soglia(g.dx, g.dy) || lancio) {
        // la carta torna al suo posto e basta: la lista resta ferma, scorre chi la usa.
        // (Una prima versione faceva anche scorrere la lista nella direzione del gesto:
        // tolta su richiesta del proprietario 01/10 — spaesava.)
        this.volaIndietro(g.dx, g.dy, this.scalaPer(g.dx, g.dy));
      } else {
        this.tornaAlCentro();
      }
    }

    // chiusura voluta dall'utente (tocco, X, Esc, rotella): la carta vola al suo posto
    chiudiZoom() {
      if (!this.dialog.open || this.chiudendo) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }
      this.zoomImg.style.transition = 'none';
      this.dialog.style.transition = 'none';
      this.zoomRiposo = this.zoomImg.getBoundingClientRect(); // ferma al centro: punto di partenza del volo
      this.dialog.classList.add('is-trascinando');            // via X e nome durante il volo
      this.volaIndietro(0, 0, 1);
    }

    annullaTrascina() {
      if (this.gesto && this.gesto.attivo) this.tornaAlCentro();
      this.gesto = null;
    }

    tornaAlCentro() {
      this.zoomImg.style.transition = 'transform 0.22s ease-out';
      this.dialog.style.transition = 'background-color 0.22s ease-out';
      this.posa(0, 0, 1, 0.8);
      this.dialog.classList.remove('is-trascinando');
      setTimeout(() => {
        if (this.chiudendo) return;
        this.zoomImg.style.transition = '';
        this.dialog.style.transition = '';
      }, 240);
    }

    /*
      La carta vola al posto della sua miniatura nella lista. La miniatura si rilegge a ogni
      fotogramma perche' intanto la pagina sta scorrendo: cosi' l'immagine atterra esattamente
      dove si trova la carta in quel momento. La miniatura resta nascosta durante il volo,
      altrimenti per un attimo se ne vedrebbero due.
    */
    volaIndietro(x, y, s) {
      this.chiudendo = true;
      const r0 = this.zoomRiposo;
      const thumb = this.zoomBtn && this.zoomBtn.querySelector('img');
      if (thumb) thumb.style.visibility = 'hidden';

      const a0 = this.alpha, t0 = performance.now(), durata = 300;
      const passo = (ora) => {
        const k = Math.min(1, (ora - t0) / durata);
        const e = 1 - Math.pow(1 - k, 3); // ease-out
        let tx = x, ty = y, ts = s * 0.6; // senza miniatura: si rimpicciolisce sul posto
        if (thumb && r0) {
          const t = thumb.getBoundingClientRect();
          tx = t.left + t.width / 2 - (r0.left + r0.width / 2);
          ty = t.top + t.height / 2 - (r0.top + r0.height / 2);
          ts = t.width / r0.width;
        }
        this.posa(x + (tx - x) * e, y + (ty - y) * e, s + (ts - s) * e, a0 * (1 - e));
        if (k < 1) requestAnimationFrame(passo);
        else this.dialog.close(); // -> ripulisciZoom
      };
      requestAnimationFrame(passo);
    }

    // a ogni chiusura (volo, X, fondo, rotella, Nascondi) tutto torna come prima
    ripulisciZoom() {
      this.chiudendo = false;
      this.gesto = null;
      this.zoomImg.style.transition = '';
      this.zoomImg.style.transform = '';
      this.dialog.style.transition = '';
      this.dialog.style.backgroundColor = '';
      this.dialog.classList.remove('is-trascinando');
      const thumb = this.zoomBtn && this.zoomBtn.querySelector('img');
      if (thumb) thumb.style.visibility = '';
    }
  }

  customElements.define('lista-carte', ListaCarte);
})();
